import React, { useState } from "react"
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip,
    Paper, Stack, Typography, List, ListItem, ListItemText, CircularProgress
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { format } from "date-fns"
import { getDateInServerTimeZone } from "../../core/utils/utils"
import { useNavigate } from "react-router-dom"
import { useGetMyExperienceRequestsQuery } from "../../core/api/experience-api"

const STATUS_CHIP = {
    WAITING: { label: "Waiting", color: "warning" },
    APPROVED: { label: "Approved", color: "success" },
    REJECTED: { label: "Rejected", color: "error" },
}

function formatDate(s) {
    try { return format(getDateInServerTimeZone(s), "dd.MM.yyyy HH:mm") } catch { return s }
}

export default function MyExperienceRequestsPage() {
    const navigate = useNavigate()
    const { data: requests = [], isLoading } = useGetMyExperienceRequestsQuery()
    const [filter, setFilter] = useState("ALL")
    const filtered = filter === "ALL" ? requests : requests.filter(r => r.status === filter)

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ maxWidth: 1000, mx: "auto", py: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">My XP requests</Typography>
                <Button variant="contained" color="warning" onClick={() => navigate(window.location.pathname.startsWith("/admin/") ? "/admin/experience/request/new" : "/experience/request/new")}>
                    New request
                </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {["ALL", "WAITING", "APPROVED", "REJECTED"].map(f => (
                    <Chip
                        key={f}
                        label={f === "ALL" ? "All" : STATUS_CHIP[f].label}
                        color={filter === f ? "warning" : "default"}
                        onClick={() => setFilter(f)}
                        variant={filter === f ? "filled" : "outlined"}
                    />
                ))}
            </Stack>

            {filtered.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">No requests yet</Typography>
                </Paper>
            ) : (
                filtered.map(r => {
                    const chip = STATUS_CHIP[r.status] || { label: r.status, color: "default" }
                    const totalXp = (r.items || []).reduce((s, i) => s + (i.xp || 0), 0)
                    return (
                        <Accordion key={r.id} disableGutters sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                                    <Chip size="small" label={chip.label} color={chip.color} />
                                    <Chip size="small" variant="outlined" label={r.categoryName || "—"} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.comment}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(r.createdAt)} · {r.items.length} people · {totalXp} XP
                                        </Typography>
                                    </Box>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                {r.adminComment && (
                                    <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                                        Admin comment: {r.adminComment}
                                    </Typography>
                                )}
                                <List dense>
                                    {r.items.map((it, i) => (
                                        <ListItem key={i} divider>
                                            <ListItemText
                                                primary={<><strong>{it.fullName || it.email}</strong> — {it.xp} XP</>}
                                                secondary={it.email}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    )
                })
            )}
        </Box>
    )
}
